import * as d3 from 'd3'

/**
 * Class used to describe a legends displayed on a chart
 */
export class ChartLegends {
  /**
   * Constructor
   * @param {*} legends
   * @param {*} legendColors
   * @param {*} svg
   * @param {*} $t
   * @param {*} config
   */
  constructor(legends, legendColors, chart, options) {
    this.options = options
    this.chart = chart
    this.legendColors = legendColors
    this.legends = legends
    this.rectSize = options.rectSize || 15
    this.spacing = options.spacing || 5
    this.checked = []
  }

  /**
   * Update config
   * @param {*} legends
   * @param {*} legendColors
   * @param {*} chart
   * @param {*} options
   */
  updateConfig(legends, legendColors, chart, options) {
    this.options = options
    this.rectSize = options.rectSize || 15
    this.spacing = options.spacing || 5
    this.chart = chart
    this.legendColors = legendColors
    this.legends = legends
    this.legend = null
  }

  /**
   * Draw legends
   */
  draw() {
    let that = this

    d3.selectAll(`#svg-${this.chart.chartId} .legend`).remove()

    //Scale legend colors
    let color = d3
      .scaleOrdinal()
      .domain(this.legends)
      .range(this.legendColors)

    if (this.chart.chartType == 'pie') {
      // If pie chart, put legend on the left
      this.legend = this.chart.svg
        .selectAll('rect')
        .data(color.domain())
        .enter()
        .append('rect')
        .attr('class', 'legend')
        .attr('x', this.chart.width - this.chart.margin.left)
        .attr('y', function(d, i) {
          return that.chart.margin.top * 2 + i * (that.rectSize + 5)
        })
        .attr('width', this.rectSize)
        .attr('height', this.rectSize)
        .style('fill', color)
        .style('stroke', function(d) {
          return d == 'all' ? '#E6E6E6' : color
        })
        .style('opacity', function(d, i) {
          return that.chart.getLegendOpacity(d, i)
        })
        .attr('checked', function(d, i) {
          return that.chart.isLegendChecked(d, i)
        })
        .attr('id', function(d) {
          return 'legend-' + d
        })
        .on('click', function(d, i) {
          let clickedLegend = d3.select(this)
          that.chart.clickOnLegend(clickedLegend, d, i)
        })

      if (
        !this.chart.config.display_options ||
        this.chart.config.display_options.legend == 'expanded'
      ) {
        this.chart.svg
          .selectAll('text')
          .data(color.domain())
          .enter()
          .append('text')
          .attr('class', 'legend-text')
          .attr('id', function(d, i) {
            return 'legend-text-' + i
          })
          .attr('x', this.chart.width - 100 + this.rectSize + this.spacing)
          .attr('y', function(d, i) {
            return that.chart.margin.top * 2 + 15 + i * (that.rectSize + 5)
          })
          .attr('font-size', this.options.textSize + 'px')
          .text(function(d) {
            return that.chart.getLegendText(d)
          })
          .attr('fill', 'white')
      } else if (this.chart.config.display_options.legend == 'compact') {
        this.initTooltip()
      }

      let maxWidth = 0
      this.chart.svg.selectAll('.legend-text').each(function() {
        let bbox = d3
          .select(this)
          .node()
          .getBBox()
        if (maxWidth < bbox.width) {
          maxWidth = bbox.width
        }
      })

      this.chart.svg
        .selectAll('.legend')
        .attr('x', this.chart.width - maxWidth - this.rectSize - this.spacing)
      this.chart.svg.selectAll('.legend-text').attr('x', this.chart.width - maxWidth)

      this.chart.svgHeight = this.chart.height + this.chart.margin.top + this.chart.margin.bottom
      d3.select('#svg-' + this.chart.chartId).attr(
        'viewBox',
        '0 0 ' + this.chart.svgWidth + ' ' + this.chart.svgHeight,
      )
    } else {
      //Add legends at the bottom of the chart
      //Add legend colors

      this.legend = this.chart.svg
        .append('g')
        .attr('class', 'chart-legends')
        .selectAll('g')
        .data(color.domain())
        .enter()
        .append('g')
        .attr('class', 'legend')

      //Rect of legends
      this.legend
        .append('rect')
        .attr('width', this.rectSize)
        .attr('height', this.rectSize)
        .attr('class', 'legendRect')
        .style('fill', color)
        .style('stroke', function(d) {
          return d == 'all' ? '#E6E6E6' : color
        })
        .style('opacity', function(d, i) {
          return that.chart.getLegendOpacity(d, i)
        })
        .attr('checked', function(d, i) {
          return that.chart.isLegendChecked(d, i)
        })
        .attr('id', function(d) {
          return 'legend-' + d
        })
        .on('click', function(d, i) {
          let clickedLegend = d3.select(this)
          that.chart.clickOnLegend(clickedLegend, d, i)
        })

      //Text of legends
      if (
        !this.chart.config.display_options ||
        this.chart.config.display_options.legend == 'expanded'
      ) {
        this.legend
          .append('text')
          .attr('id', function(d, i) {
            return 'legend-text-' + i
          })
          .attr('x', this.rectSize + this.spacing)
          .attr('y', this.rectSize - this.spacing / 2)
          .attr('font-size', this.options.textSize + 'px')
          .text(function(d) {
            return that.chart.getLegendText(d)
          })
      } else if (this.chart.config.display_options.legend == 'compact') {
        this.initTooltip()
      }

      let row = 0
      let maxLegendWidth = 0

      //Calculate max legend width displayed
      d3.selectAll(`#svg-${this.chart.chartId} .legend`).each(function() {
        if (maxLegendWidth < this.getBBox().width) {
          maxLegendWidth = this.getBBox().width
        }
      })

      maxLegendWidth = maxLegendWidth + (maxLegendWidth * 10) / 100

      //Calculate available columns and rows for legends in the current chart
      let availableColumns = Math.floor(
        (this.chart.svgWidth - this.chart.margin.right) / maxLegendWidth,
      )
      let availableRows = Math.ceil(this.legends.length / availableColumns)

      this.legend.attr('transform', function(d, i) {
        row = i % availableRows == 0 ? 0 : row + 1
        let x = Math.floor(i / availableRows) * maxLegendWidth + that.spacing
        let y = that.chart.height + that.chart.margin.bottom + row * that.rectSize
        return 'translate(' + x + ',' + y + ')'
      })

      this.rows = availableRows
      this.columns = availableColumns

      this.chart.svgHeight =
        this.chart.height +
        this.chart.margin.top +
        this.chart.margin.bottom * 2 +
        availableRows * this.rectSize
      d3.select('#svg-' + this.chart.chartId).attr(
        'viewBox',
        '0 0 ' + this.chart.svgWidth + ' ' + this.chart.svgHeight,
      )
    }
  }

  /**
   * Init tootlip for chart's legend on over
   */
  initTooltip() {
    if (this.chart.config.display_options && this.chart.config.display_options.legend !== 'none') {
      this.legend.on('mouseover', this.chart.tooltip.show).on('mouseout', this.chart.tooltip.hide)
    }
  }
}
